import { SxProps, Tab, Tabs, TabsProps, Theme } from '@mui/material'
import { always, equals, identity, ifElse } from 'ramda'
import { ReactNode, useState } from 'react'

import { useUrlParams } from '@/hooks'

import { LinkHref } from '../Link'

export interface ITab {
  name: string
  component: ReactNode
}
export interface EditTabsPatternProps<T extends ITab> {
  tabs: T[]
  defaultTab: T['name']
  showTabs: boolean
  tabTitle?: string
  sx?: SxProps<Theme>
  variant?: TabsProps['variant']
  urlParams?: boolean
}

const getTabValue = ifElse(equals(-1), always(0), identity)
/**
 * This pattern will have all of the tabs for any edit page. The tabs are going to be rendered based if "showTabs" is true or false.
 * If "showTabs" is false, then the default tab will be rendered.
 * If "showTabs" is true, then all of the tabs will be rendered.
 *
 * @example
 * <EditTabsPattern
 *   tabs={[
 *     { name: "Tab 1", component: <div>Tab 1</div> },
 *     { name: "Tab 2", component: <div>Tab 2</div> },
 *   ]}
 *   defaultTab="Tab 1"
 *   showTabs={false}
 * />
 *
 *
 * @param {Object} props - The props of the component.
 * @param {ITab[]} props.tabs - The list of tabs to render.
 * @param {string} props.defaultTab - The default tab to render.
 * @param {boolean} props.showTabs - Whether to show the tabs or not.
 */
export function EditTabsPattern<T extends ITab>(
  props: EditTabsPatternProps<T>
) {
  const { tabs, defaultTab, showTabs = false, urlParams = true } = props
  const [memoryTab, setMemoryTab] = useState(defaultTab)
  const [{ tab: urlTab }] = useUrlParams()
  const tab = urlParams ? urlTab : memoryTab
  const defaultTabComponent = tabs.find((t) => t.name === defaultTab)?.component
  const selectedTab = tabs.findIndex((t) => t.name === tab)
  const activeComponent =
    selectedTab >= 0 ? tabs[selectedTab]?.component : defaultTabComponent

  if (!showTabs) return <>{defaultTabComponent}</>

  return (
    <>
      <Tabs
        id="tabs"
        sx={{ mb: 2, overflowX: 'auto', ...(props.sx || {}) }}
        value={getTabValue(selectedTab)}
        variant={props.variant}
      >
        {tabs.map(({ name: tab }, index) => (
          <Tab
            key={tab}
            {...(urlParams
              ? { LinkComponent: LinkHref, href: `?tab=${tab}` }
              : { onClick: () => setMemoryTab(tab) })}
            id={`tab-${tab}`.toLowerCase()}
            label={tab}
            value={index}
          />
        ))}
      </Tabs>
      {activeComponent}
    </>
  )
}
